// Utility for managing SQL query persistence in localStorage by datasource

export interface PersistedQueryResult {
  columns: string[]
  rows: unknown[][]
  rowCount: number
  executionTime: number
  executionTimeMs?: number
  status?: 'success' | 'error'
  message?: string
  query: string
  timestamp: string // Store as ISO string for JSON serialization
  affectedRows?: number
}

export interface TabState {
  id: string
  label: string
  content: string
  isDirty?: boolean
  savedQueryId?: string
  savedQueryCollectionId?: string
  savedQueryName?: string
  databaseName?: string
  schemaName?: string
}

export interface DatasourceQueryState {
  sqlQuery: string
  selectedDatabase: string
  selectedSchema?: string
  queryResults: PersistedQueryResult[]
  lastModified: string
  tabs?: TabState[]
  activeTabId?: string
}

const STORAGE_KEY_PREFIX = 'query_editor_state_'
const MAX_RESULTS_PER_DATASOURCE = 50

// Get storage key for a specific datasource
function getStorageKey(datasourceId: string): string {
  return `${STORAGE_KEY_PREFIX}${datasourceId}`
}

// Save query state for a specific datasource with improved validation
export function saveQueryState(
  datasourceId: string,
  sqlQuery: string,
  selectedDatabase: string,
  selectedSchema?: string,
  queryResults: PersistedQueryResult[] = []
): void {
  try {
    if (!datasourceId) {
      console.warn('[saveQueryState] Invalid datasourceId');
      return;
    }
    
    // Convert query results to persistable format (limit to MAX_RESULTS_PER_DATASOURCE)
    const persistedResults: PersistedQueryResult[] = queryResults
      .slice(0, MAX_RESULTS_PER_DATASOURCE)
      .map(result => ({
        columns: result.columns,
        rows: result.rows,
        rowCount: result.rowCount || 0,
        executionTime: result.executionTime || 0,
        executionTimeMs: result.executionTimeMs,
        status: result.status,
        message: result.message,
        query: result.query || '',
        timestamp: result.timestamp || new Date().toISOString(),
        affectedRows: result.affectedRows
      }));

    // Load existing state to preserve tabs and other data
    const existing = loadQueryState(datasourceId);
    
    const state: DatasourceQueryState = {
      sqlQuery: sqlQuery || '',
      selectedDatabase: selectedDatabase || '',
      selectedSchema,
      queryResults: persistedResults,
      lastModified: new Date().toISOString(),
      // Preserve existing tabs and activeTabId with validation
      tabs: existing?.tabs || [],
      activeTabId: existing?.activeTabId
    };

    localStorage.setItem(getStorageKey(datasourceId), JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save query state to localStorage:', error);
  }
}

// Load query state for a specific datasource
export function loadQueryState(datasourceId: string): DatasourceQueryState | null {
  try {
    const stored = localStorage.getItem(getStorageKey(datasourceId))
    if (!stored) return null

    const state: unknown = JSON.parse(stored)
    
    // Validate the parsed state is an object
    if (typeof state !== 'object' || state === null) return null
    
    const s: Record<string, unknown> = ((): Record<string, unknown> => {
      const maybe = state as unknown
      return (typeof maybe === 'object' && maybe !== null && !Array.isArray(maybe)) ? (maybe as Record<string, unknown>) : {}
    })()
    const typedState: DatasourceQueryState = {
      sqlQuery: typeof s.sqlQuery === 'string' ? s.sqlQuery : '',
      selectedDatabase: typeof s.selectedDatabase === 'string' ? s.selectedDatabase : '',
      selectedSchema: typeof s.selectedSchema === 'string' ? s.selectedSchema : undefined,
      queryResults: Array.isArray(s.queryResults) ? s.queryResults.filter((r): r is PersistedQueryResult => typeof r === 'object' && r !== null) : [],
      lastModified: typeof s.lastModified === 'string' ? s.lastModified : new Date().toISOString(),
      tabs: Array.isArray(s.tabs) ? s.tabs.filter((t): t is TabState => typeof t === 'object' && t !== null) : [],
      activeTabId: typeof s.activeTabId === 'string' ? s.activeTabId : undefined,
    }
    
    // Ensure queryResults exists and is an array for backwards compatibility
    if (!Array.isArray(typedState.queryResults)) {
      typedState.queryResults = []
    }
    
    // Keep timestamp as string for consistency with interface
    const convertedResults = typedState.queryResults.map(result => ({
      ...result,
      timestamp: result.timestamp // Keep as string
    }))

    return {
      ...typedState,
      queryResults: convertedResults as PersistedQueryResult[]
    }
  } catch (error) {
    console.warn('Failed to load query state from localStorage:', error)
    return null
  }
}

// Save only the SQL query for a datasource (for real-time saving as user types)
export function saveQueryText(datasourceId: string, sqlQuery: string): void {
  try {
    const existing = loadQueryState(datasourceId)
    if (existing) {
      // Update only the SQL query, keep other state
      saveQueryState(
        datasourceId, 
        sqlQuery, 
        existing.selectedDatabase, 
        existing.selectedSchema, 
        existing.queryResults
      )
    } else {
      // Create new state with just the query
      saveQueryState(datasourceId, sqlQuery, '', undefined, [])
    }
  } catch (error) {
    console.warn('Failed to save query text to localStorage:', error)
  }
}

// Save query results while preserving other state
export function saveQueryResults(
  datasourceId: string,
  queryResults: PersistedQueryResult[],
  currentQuery?: string,
  currentDatabase?: string,
  currentSchema?: string
): void {
  try {
    const existing = loadQueryState(datasourceId)
    saveQueryState(
      datasourceId,
      currentQuery || existing?.sqlQuery || '',
      currentDatabase || existing?.selectedDatabase || '',
      currentSchema || existing?.selectedSchema,
      queryResults
    )
  } catch (error) {
    console.warn('Failed to save query results to localStorage:', error)
  }
}

// Clear stored state for a specific datasource
export function clearQueryState(datasourceId: string): void {
  try {
    localStorage.removeItem(getStorageKey(datasourceId))
  } catch (error) {
    console.warn('Failed to clear query state from localStorage:', error)
  }
}

// Get all stored datasource IDs (for cleanup or debugging)
export function getStoredDatasourceIds(): string[] {
  const ids: string[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        ids.push(key.substring(STORAGE_KEY_PREFIX.length))
      }
    }
  } catch (error) {
    console.warn('Failed to get stored datasource IDs:', error)
  }
  return ids
}

// Debug function to check what's stored for a datasource
export function debugStoredTabs(datasourceId: string): void {
  console.log(`[DEBUG] Checking stored data for datasource: ${datasourceId}`)
  const key = getStorageKey(datasourceId)
  const stored = localStorage.getItem(key)
  if (stored) {
    try {
      const parsed: unknown = JSON.parse(stored)
      if (typeof parsed === 'object' && parsed !== null) {
        const typedParsed = parsed as { tabs?: unknown; activeTabId?: unknown }
        console.log('[DEBUG] Stored data:', parsed)
        console.log('[DEBUG] Tabs:', typedParsed.tabs)
        console.log('[DEBUG] Active tab ID:', typedParsed.activeTabId)
      }
    } catch (e) {
      console.error('[DEBUG] Failed to parse stored data:', e)
    }
  } else {
    console.log('[DEBUG] No data found for this datasource')
  }
}

// Cleanup old or orphaned states (call this periodically or on app startup)
export function cleanupOldStates(validDatasourceIds: string[]): void {
  try {
    const storedIds = getStoredDatasourceIds()
    const idsToRemove = storedIds.filter(id => !validDatasourceIds.includes(id))
    
    for (const id of idsToRemove) {
      clearQueryState(id)
    }
    
    if (idsToRemove.length > 0) {
      console.log(`Cleaned up ${idsToRemove.length} orphaned query states`)
    }
  } catch (error) {
    console.warn('Failed to cleanup old query states:', error)
  }
}

// Save tabs state for a datasource with improved synchronization
export function saveTabs(
  datasourceId: string,
  tabs: TabState[],
  activeTabId: string
): void {
  try {
    if (!datasourceId || tabs.length === 0) {
      console.warn('[saveTabs] Invalid input parameters');
      return;
    }
    
    
    const existing = loadQueryState(datasourceId);
    const activeTab = tabs.find(t => t.id === activeTabId);
    
    if (existing) {
      // Update tabs while preserving other state
      const updatedState: DatasourceQueryState = {
        ...existing,
        tabs: tabs.map(tab => ({
          id: tab.id,
          label: tab.label,
          content: tab.content,
          isDirty: tab.isDirty,
          savedQueryId: tab.savedQueryId,
          savedQueryCollectionId: tab.savedQueryCollectionId,
          savedQueryName: tab.savedQueryName
        })),
        activeTabId,
        // Update sqlQuery to match the active tab's content
        sqlQuery: activeTab?.content || existing.sqlQuery,
        lastModified: new Date().toISOString()
      };
      localStorage.setItem(getStorageKey(datasourceId), JSON.stringify(updatedState));
    } else {
      // Create new state with tabs
      const newState: DatasourceQueryState = {
        sqlQuery: activeTab?.content || '',
        selectedDatabase: '',
        selectedSchema: undefined,
        queryResults: [],
        lastModified: new Date().toISOString(),
        tabs: tabs.map(tab => ({
          id: tab.id,
          label: tab.label,
          content: tab.content,
          isDirty: tab.isDirty,
          savedQueryId: tab.savedQueryId,
          savedQueryCollectionId: tab.savedQueryCollectionId,
          savedQueryName: tab.savedQueryName
        })),
        activeTabId
      };
      localStorage.setItem(getStorageKey(datasourceId), JSON.stringify(newState));
    }
  } catch (error) {
    console.error('[saveTabs] Failed to save tabs to localStorage:', error);
  }
}

// Load tabs state for a datasource with better validation
export function loadTabs(datasourceId: string): { tabs: TabState[], activeTabId: string } | null {
  try {
    if (!datasourceId) {
      return null;
    }
    
    const state = loadQueryState(datasourceId);
    if (state && state.tabs && state.tabs.length > 0) {
      // Validate and restore saved query info
      const validatedTabs = state.tabs.map(tab => ({
        id: tab.id || '1',
        label: tab.label || 'Query',
        content: tab.content || '',
        // If it has saved query info, it's not dirty (it matches the saved version)
        isDirty: tab.savedQueryId ? false : (tab.isDirty || false),
        savedQueryId: tab.savedQueryId,
        savedQueryCollectionId: tab.savedQueryCollectionId,
        savedQueryName: tab.savedQueryName
      }));
      
      // Validate that the stored activeTabId actually exists in the tabs array
      const validActiveTabId = state.activeTabId && validatedTabs.some(tab => tab.id === state.activeTabId)
        ? state.activeTabId
        : validatedTabs[0].id;
      
      return {
        tabs: validatedTabs,
        activeTabId: validActiveTabId
      };
    }
    return null;
  } catch (error) {
    console.error('[loadTabs] Failed to load tabs from localStorage:', error);
    return null;
  }
}